import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Gantt, Task, ViewMode } from "gantt-task-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState, useMemo } from "react";
import { CircularProgressbar } from "react-circular-progressbar";
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary";
import { LoadingEllipsis } from "../../../shared/components/loading/Loading";
import { useAppContext } from "../../../shared/functions/Context";
import { ganttChartProjects } from "../data/itemsTypes";
import { ProjectBudgetChart, ProjectStatusChart } from "../utils/charts";
import { getDefaultView, getProgressColors, projectsMilestonesTotal, projectProjectStatistics, projectTotalBudget } from "../utils/common";
import Filter from "../utils/filter";
import { moneyFormat } from "../utils/formats";
import GanttChartAction from "./ganttViewActions";
import "gantt-task-react/dist/index.css";

const CompanyProjectStatistics = observer(() => {
    const { api, store } = useAppContext();
    
    const [selectedValue, setSelectedValue] = useState("all");
    const [view, setView] = useState<ViewMode>(getDefaultView());
    const [isChecked, setIsChecked] = useState(localStorage.getItem("active-list") === "true");
    const [loading, setLoading] = useState(false);
    
    const departmentList = useMemo(() => store.department.all.map(d => ({ id: d.asJson.id, name: d.asJson.name })), [store.department.all]);
    const departmentName = selectedValue !== "all" ? store.department.getItemById(selectedValue)?.asJson.name : "Company";
    
    const filteredProjects = useMemo(() => store.projectManagement.all
        .map(p => p.asJson)
        .filter(project => selectedValue === "all" || project.department === selectedValue), [store.projectManagement.all, selectedValue]);

    const filteredMilestones = useMemo(() => store.projectTask.all
        .map(m => m.asJson)
        .filter(milestone => selectedValue === "all" || milestone.departmentId === selectedValue), [store.projectTask.all, selectedValue]);
    
    const { status, completionRate } = projectProjectStatistics(filteredProjects);
    const projectTimeline: Task[] = ganttChartProjects(filteredProjects);
    const projectBudget = projectTotalBudget(filteredProjects);
    const milestoneBudget = projectsMilestonesTotal(filteredMilestones);
    const remainingAmount = projectBudget - milestoneBudget;
    
    const columnWidth = {
        [ViewMode.Year]: 350,
        [ViewMode.Month]: 300,
        [ViewMode.Week]: 250,
        [ViewMode.Day]: 200,
        [ViewMode.HalfDay]: 150,
        [ViewMode.QuarterDay]: 100,
        [ViewMode.Hour]: 65,
    }[view] || 65;

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    api.projectManagement.getAllProjects(),
                    api.projectManagement.getAllMilestones(filteredProjects),
                    store.department.all.length < 1 && api.department.getAll()
                ]);
            } catch (error) {
                console.error("Error fetching data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [api.projectManagement, api.department, store.department.all.length]);

    if (loading) return <LoadingEllipsis fullHeight />;

    return (
        <ErrorBoundary>
            <div className="gantt-actions">
                <button className="btn btn-primary" type="button">
                    <span>Filter&nbsp;&nbsp;</span>
                    <FontAwesomeIcon icon={faFilter} className="icon uk-margin-small-right" />
                </button>
                <div uk-drop="mode: click">
                    <Filter list={[...departmentList, { name: "All Departments", id: "all" }]} selectedValue={selectedValue} setSelectedValue={setSelectedValue} />
                </div>
            </div>
            <div className="dep-content">
                <div className="basic-statistics">
                    <div className="s-item">
                        <div className="content">
                            <span>{departmentName} Projects Budget</span><br />
                            <span style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#84cbe9" }}>NAD {moneyFormat(projectBudget)}</span>
                        </div>
                        <div className="content">
                            <span>Spent</span><br />
                            <span  style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#e7a637" }}>NAD {moneyFormat(milestoneBudget)}</span>
                        </div>
                        <div className="content">
                            <span>Remaining</span><br />
                            <span style={{ fontSize: "0.8rem", fontWeight: "bold", color: remainingAmount < projectBudget ? "#84cbe9" : "#ff595e" }}>NAD {moneyFormat(remainingAmount)}</span>
                        </div>
                    </div>
                    <div className="s-item">
                        <div className="content">
                            <span style={{ fontSize: "2rem", fontWeight: "bold" }}>{Math.round(completionRate * 100)}%</span><br />
                            <span>{departmentName} Projects Progress</span>
                        </div>
                    </div>
                    <div className="s-item">
                        <div className="progress-bar">
                            <CircularProgressbar 
                                value={completionRate} 
                                maxValue={1} 
                                text={`${status.completed}/${filteredProjects.length}`}
                                styles={{
                                    path: { stroke: getProgressColors(completionRate * 100) },
                                    text: { fill: getProgressColors(completionRate * 100), fontSize: '1.5rem' }
                                }}
                            />
                        </div>
                        <div className="content">
                            <span>{departmentName} Completed Projects</span>
                        </div>
                    </div>
                </div>
                <div className="graphs">
                    <div className="left">
                        <h4 className="graph-title">{departmentName} Projects Budget</h4>
                        <ProjectBudgetChart projects={filteredProjects} />
                    </div>
                    <div className="right">
                        <h4 className="graph-title">Projects Status</h4>
                        <ProjectStatusChart status={status} />
                    </div>
                </div>
                <div className="projects-timeline">
                    <h4 className="graph-title">Projects Timeline</h4>
                    <GanttChartAction onViewListChange={setIsChecked} onViewModeChange={setView} isChecked={isChecked} />
                    <div className="gantt-chart">
                        {!!projectTimeline.length && 
                            <Gantt tasks={projectTimeline} viewMode={view} listCellWidth={isChecked ? "155px" : ""} columnWidth={columnWidth} />
                        }
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
});

export default CompanyProjectStatistics;
